"use client";

import React from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { ClientPreferences, PreferenceProvenanceEntry } from '@/types/preferences';

interface PreferenceConfirmBannerProps {
  preferences: Partial<ClientPreferences>;
  mode?: 'current' | 'suggestion';
  provenance?: Record<string, PreferenceProvenanceEntry | undefined>;
  confidence?: number;
  explanation?: string;
  onConfirm: () => void;
  onChange: () => void;
  onReject?: () => void;
  className?: string;
}

const summarizePreferences = (preferences: Partial<ClientPreferences>): string => {
  const parts: string[] = [];
  if (preferences.spokesperson?.name) {
    parts.push(`Spokesperson = ${preferences.spokesperson.name}`);
  }
  if (preferences.tone) {
    parts.push(`Tone = ${preferences.tone}`);
  }
  if (preferences.mediaContact?.email || preferences.mediaContact?.name) {
    parts.push(`Media contact = ${preferences.mediaContact.email || preferences.mediaContact.name}`);
  }
  return parts.join(', ');
};

const formatSource = (source?: string): string => {
  if (!source) return '';
  if (source === 'session_override') return 'session';
  if (source === 'user_client_explicit') return 'explicit';
  if (source === 'user_client_inferred') return 'inferred';
  if (source === 'client_baseline') return 'client baseline';
  if (source === 'stylebook_default') return 'stylebook';
  return source.replace(/_/g, ' ');
};

export function PreferenceConfirmBanner({
  preferences,
  mode = 'current',
  provenance,
  confidence,
  explanation,
  onConfirm,
  onChange,
  onReject,
  className,
}: PreferenceConfirmBannerProps) {
  const summary = summarizePreferences(preferences);
  if (!summary) return null;
  const provenanceSummary = provenance
    ? Array.from(
        new Set(
          Object.values(provenance)
            .filter(Boolean)
            .map((entry) => formatSource(entry?.source))
            .filter(Boolean)
        )
      )
        .slice(0, 2)
        .join(', ')
    : '';

  const title = mode === 'suggestion' ? 'Detected preference update' : 'Using client preferences';
  const confirmLabel = mode === 'suggestion' ? 'Apply' : 'Confirm';
  const changeLabel = mode === 'suggestion' ? 'Edit' : 'Change';
  const description = mode === 'suggestion'
    ? 'A preference signal was extracted from your edit.'
    : 'These preferences are currently applied to new drafts.';

  return (
    <div className={cn('rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">{title}</p>
          <p className="text-xs text-emerald-800/80 dark:text-emerald-100/80">{description}</p>
          <p className="text-xs text-emerald-800/80 dark:text-emerald-100/80">{summary}</p>
          {(typeof confidence === 'number' || provenanceSummary || explanation) && (
            <p className="text-[11px] text-emerald-900/80 dark:text-emerald-100/80 mt-1">
              {typeof confidence === 'number' ? `Confidence ${confidence.toFixed(2)}` : ''}
              {typeof confidence === 'number' && provenanceSummary ? ' · ' : ''}
              {provenanceSummary ? `Source ${provenanceSummary}` : ''}
              {(typeof confidence === 'number' || provenanceSummary) && explanation ? ' · ' : ''}
              {explanation ? explanation : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onConfirm}>
            {confirmLabel}
          </Button>
          <Button size="sm" onClick={onChange}>
            {changeLabel}
          </Button>
          {mode === 'suggestion' && onReject && (
            <Button variant="ghost" size="sm" onClick={onReject}>
              Ignore
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
